/**
 * Brand Voice Service
 *
 * API service module for brand voice profiles management.
 */

import { apiClient, extractData } from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FormalityLevel = 'very_casual' | 'casual' | 'neutral' | 'formal' | 'very_formal';
export type EmojiUsage = 'none' | 'minimal' | 'moderate' | 'frequent';
export type BrandVoiceTone = 'professional' | 'casual' | 'friendly' | 'authoritative' | 'humorous' | 'inspirational';

export interface BrandVoiceProfileUploader {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export interface BrandVoiceDisclaimer {
  text: string;
  triggerCondition?: string;
}

export interface BrandVoiceSamplePost {
  content: string;
  platform: string;
}

export interface BrandVoiceProfile {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  tone: string | null;
  formalityLevel: FormalityLevel;
  emojiUsage: EmojiUsage;
  dos: string[];
  donts: string[];
  samplePosts: BrandVoiceSamplePost[];
  styleGuidelines: Record<string, unknown>;
  hashtagPreferences: Record<string, unknown>;
  vocabularyPreferences: Record<string, unknown>;
  mandatoryDisclaimers: BrandVoiceDisclaimer[];
  isDefault: boolean;
  createdBy: BrandVoiceProfileUploader | null;
  createdAt: string;
  updatedAt: string;
}

export interface BrandVoicesSummary {
  totalProfiles: number;
  defaultProfileId: string | null;
}

export interface ListBrandVoicesParams {
  workspaceId: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'createdAt' | 'name' | 'isDefault';
  sortOrder?: 'asc' | 'desc';
}

export interface ListBrandVoicesResponse {
  profiles: BrandVoiceProfile[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  summary: BrandVoicesSummary;
}

export interface SetDefaultResponse {
  id: string;
  isDefault: boolean;
  message: string;
}

export interface DuplicateResponse {
  id: string;
  name: string;
  message: string;
}

export interface DeleteBrandVoiceResponse {
  id: string;
  name: string;
  message: string;
  impactSummary: {
    campaignsUsingVoice: number;
    postsUsingVoice: number;
  };
}

export interface BrandVoiceUsageImpact {
  campaignsUsingVoice: number;
  postsUsingVoice: number;
}

export interface CreateBrandVoicePayload {
  name: string;
  description?: string;
  tone: BrandVoiceTone;
  formalityLevel: FormalityLevel;
  emojiUsage: EmojiUsage;
  styleGuidelines?: string | Record<string, unknown>;
  hashtagPreferences?: Record<string, unknown>;
  vocabularyPreferences?: {
    preferredTerms?: string[];
    avoidedTerms?: string[];
    [key: string]: unknown;
  };
  dos?: string[];
  donts?: string[];
  mandatoryDisclaimers?: BrandVoiceDisclaimer[];
  samplePosts?: BrandVoiceSamplePost[];
  isDefault?: boolean;
}

export type UpdateBrandVoicePayload = Partial<CreateBrandVoicePayload>;

// ─── AI Generate Types ──────────────────────────────────────────────────────

export interface AIGenerateBrandVoiceRequest {
  name?: string;
  industry?: string;
  targetAudience?: string;
  toneHints?: string[];
  additionalInstructions?: string;
  saveImmediately?: boolean;
}

export interface AIGeneratedBrandVoiceData {
  name: string;
  description: string;
  tone: string;
  formalityLevel: string;
  emojiUsage: string;
  dos: string[];
  donts: string[];
  samplePosts: string[];
  styleGuidelines: Record<string, unknown>;
  hashtagPreferences: Record<string, unknown>;
  vocabularyPreferences: Record<string, unknown>;
  mandatoryDisclaimers: string[];
  knowledgeContextUsed: string[];
}

export interface AIGenerateBrandVoiceResult {
  brandVoice: AIGeneratedBrandVoiceData;
  id?: string;
  saved: boolean;
  sourcesUsed: { documentId: string; documentTitle: string; chunkCount: number }[];
  confidenceScore: number;
  message: string;
}

// ─── Brand Voice Service ────────────────────────────────────────────────────

export const brandVoiceService = {
  /**
   * List brand voice profiles for a workspace.
   */
  list: async (params: ListBrandVoicesParams): Promise<ListBrandVoicesResponse> => {
    const response = await apiClient.get(
      `/workspaces/${params.workspaceId}/brand-voices`,
      {
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      }
    );

    const body = response.data as {
      success: boolean;
      data: BrandVoiceProfile[];
      meta?: {
        pagination?: ListBrandVoicesResponse['pagination'];
        summary?: BrandVoicesSummary;
      };
    };

    return {
      profiles: body.data || [],
      pagination: body.meta?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      },
      summary: body.meta?.summary || {
        totalProfiles: 0,
        defaultProfileId: null,
      },
    };
  },

  /**
   * Get a single brand voice profile by ID.
   */
  getById: async (workspaceId: string, profileId: string): Promise<BrandVoiceProfile> => {
    return extractData(
      apiClient.get<BrandVoiceProfile>(
        `/workspaces/${workspaceId}/brand-voices/${profileId}`
      )
    );
  },

  /**
   * Create a new brand voice profile.
   */
  create: async (workspaceId: string, payload: CreateBrandVoicePayload): Promise<BrandVoiceProfile> => {
    return extractData(
      apiClient.post<BrandVoiceProfile>(
        `/workspaces/${workspaceId}/brand-voices`,
        payload
      )
    );
  },

  /**
   * Update an existing brand voice profile.
   */
  update: async (workspaceId: string, profileId: string, payload: UpdateBrandVoicePayload): Promise<BrandVoiceProfile> => {
    return extractData(
      apiClient.put<BrandVoiceProfile>(
        `/workspaces/${workspaceId}/brand-voices/${profileId}`,
        payload
      )
    );
  },

  /**
   * Set a brand voice profile as the default.
   */
  setDefault: async (workspaceId: string, profileId: string): Promise<SetDefaultResponse> => {
    return extractData(
      apiClient.put<SetDefaultResponse>(
        `/workspaces/${workspaceId}/brand-voices/${profileId}/set-default`
      )
    );
  },

  /**
   * Duplicate a brand voice profile.
   */
  duplicate: async (workspaceId: string, profileId: string): Promise<DuplicateResponse> => {
    return extractData(
      apiClient.post<DuplicateResponse>(
        `/workspaces/${workspaceId}/brand-voices/${profileId}/duplicate`
      )
    );
  },

  /**
   * Get usage impact for a brand voice profile (pre-delete check).
   */
  getUsageImpact: async (workspaceId: string, profileId: string): Promise<BrandVoiceUsageImpact> => {
    return extractData(
      apiClient.get<BrandVoiceUsageImpact>(
        `/workspaces/${workspaceId}/brand-voices/${profileId}/usage`
      )
    );
  },

  /**
   * Delete a brand voice profile.
   */
  delete: async (workspaceId: string, profileId: string): Promise<DeleteBrandVoiceResponse> => {
    return extractData(
      apiClient.delete<DeleteBrandVoiceResponse>(
        `/workspaces/${workspaceId}/brand-voices/${profileId}`
      )
    );
  },

  /**
   * AI-generate a brand voice profile using knowledge base context.
   */
  aiGenerate: async (workspaceId: string, data: AIGenerateBrandVoiceRequest): Promise<AIGenerateBrandVoiceResult> => {
    return extractData(
      apiClient.post<AIGenerateBrandVoiceResult>(
        `/workspaces/${workspaceId}/brand-voices/ai-generate`,
        data
      )
    );
  },
};